import { useEffect, useState, useContext } from "react";
import ListAndAddTabs from "components/Layout/ListAndAddTabs";
import Page from "components/Page";
import LogbooksTable from "Modules/Logbooks/LogbooksTable";
import { BuildingContext } from "Context/BuildingProvider";
import BasicLogbookConfig, {
  LogbookTimeType,
} from "Modules/Logbooks/BasicLogbookConfig";
import StepperFrame, {
  InvalidationCheck,
  StepFrame,
} from "components/Layout/StepperFrame";
import LogbookItemAdder from "Modules/Logbooks/LogbookItemAdder";
import { FormOption } from "components/Layout/ConfigForm";
import { INSERT_LOGBOOK } from "lib/logbooks";
import { useMutation } from "@apollo/client";
import { InsertLogbookMutationVariables } from "generated/graphql";
import { useHistory } from "react-router";
import { AuthContext } from "Firebase/AuthProvider";

interface LogbookObject {
  title: string;
  timeType: LogbookTimeType;
  textInputs: FormOption[];
  photoInputs: FormOption[];
  barcodeInputs: FormOption[];
  signatureInputs: FormOption[];
}
interface LogbooksState {
  editing: boolean;
  currentLogbook: LogbookObject;
  tabValue: number;
}
export enum LogbookItemType {
  TEXT = "text",
  PHOTO = "photo",
  SIGNATURE = "signature",
  BARCODE = "barcode",
  TEXT_SCAN = "textScan",
}

const DEFAULT_STATE: LogbooksState = {
  editing: false,
  tabValue: 0,
  currentLogbook: {
    title: "",
    timeType: LogbookTimeType.SIGN_IN_OUT,
    textInputs: [],
    photoInputs: [],
    barcodeInputs: [],
    signatureInputs: [],
  },
};

export default function Logbooks() {
  const [state, setState] = useState(DEFAULT_STATE);
  const list: JSX.Element = <LogbooksTable />;
  const { currentBuilding } = useContext(BuildingContext);
  const { setRoute } = useContext(AuthContext);
  useEffect(() => {
    setRoute("Logbooks");
  }, []);
  const [insertLogbook, { data }] =
    useMutation<InsertLogbookMutationVariables>(INSERT_LOGBOOK);
  const history = useHistory();
  const handleLogbookNameChange = (name: string) => {
    setState({
      ...state,
      currentLogbook: { ...state.currentLogbook, title: name },
    });
  };

  const handleLogbookTimeTypeChange = (timeType: LogbookTimeType) => {
    setState({
      ...state,
      currentLogbook: { ...state.currentLogbook, timeType: timeType },
    });
  };

  const onTextOptionAdd = (option: FormOption) => {
    const options = state.currentLogbook.textInputs;
    options.push(option);
    setState({
      ...state,
      currentLogbook: { ...state.currentLogbook, textInputs: options },
    });
  };

  const onTextOptionEdit = (option: FormOption) => {
    const options = state.currentLogbook.textInputs;
  };

  const onTextOptionRemove = (option: FormOption) => {
    const options = state.currentLogbook.textInputs;
  };

  const onPhotoOptionAdd = (option: FormOption) => {
    const options = state.currentLogbook.photoInputs;
    options.push(option);
    setState({
      ...state,
      currentLogbook: { ...state.currentLogbook, photoInputs: options },
    });
  };

  const onPhotoOptionEdit = (option: FormOption) => {
    const options = state.currentLogbook.photoInputs;
  };

  const onPhotoOptionRemove = (option: FormOption) => {
    const options = state.currentLogbook.photoInputs;
  };

  const onSignatureOptionAdd = (option: FormOption) => {
    const options = state.currentLogbook.signatureInputs;
    options.push(option);
    setState({
      ...state,
      currentLogbook: { ...state.currentLogbook, signatureInputs: options },
    });
  };

  const onSignatureOptionEdit = (option: FormOption) => {
    const options = state.currentLogbook.signatureInputs;
  };

  const onSignatureOptionRemove = (option: FormOption) => {
    const options = state.currentLogbook.signatureInputs;
  };

  const onBarcodeOptionAdd = (option: FormOption) => {
    const options = state.currentLogbook.barcodeInputs;
    options.push(option);
    setState({
      ...state,
      currentLogbook: { ...state.currentLogbook, barcodeInputs: options },
    });
  };

  const onBarcodeOptionEdit = (option: FormOption) => {
    const options = state.currentLogbook.barcodeInputs;
  };

  const onBarcodeOptionRemove = (option: FormOption) => {
    const options = state.currentLogbook.barcodeInputs;
  };

  const checkIfLogBookNameIsInvalid = (): InvalidationCheck => {
    let invalid = false;
    let message = "";
    if (state.currentLogbook.title === "") {
      invalid = true;
      message = "Please add a name for the logbook";
    }
    return { invalid, message };
  };

  const checkIfLogBookConfigIsInvalid = (): InvalidationCheck => {
    let invalid = false;
    let message = "";
    if (
      state.currentLogbook.barcodeInputs.length === 0 &&
      state.currentLogbook.photoInputs.length === 0 &&
      state.currentLogbook.signatureInputs.length === 0 &&
      state.currentLogbook.textInputs.length === 0
    ) {
      invalid = true;
      message = "Please add atleast one input to Logbook";
    }
    return { invalid, message };
  };

  const stepFrames: StepFrame[] = [
    {
      label: "Name and Configure Logbook",
      inValid: checkIfLogBookNameIsInvalid(),
      children: (
        <BasicLogbookConfig
          name={state.currentLogbook.title}
          timeType={state.currentLogbook.timeType}
          handleLogbookNameChange={handleLogbookNameChange}
          handleLogbookTimeTypeChange={handleLogbookTimeTypeChange}
        />
      ),
    },
    {
      label: "Add logbook Items",
      inValid: checkIfLogBookConfigIsInvalid(),
      children: (
        <LogbookItemAdder
          barcodeOptions={state.currentLogbook.barcodeInputs}
          signatureOptions={state.currentLogbook.signatureInputs}
          photoOptions={state.currentLogbook.photoInputs}
          textOptions={state.currentLogbook.textInputs}
          onTextOptionEdit={onTextOptionEdit}
          onTextOptionRemove={onTextOptionRemove}
          onTextOptionAdd={onTextOptionAdd}
          onPhotoOptionEdit={onPhotoOptionEdit}
          onPhotoOptionRemove={onPhotoOptionRemove}
          onPhotoOptionAdd={onPhotoOptionAdd}
          onSignatureOptionEdit={onSignatureOptionEdit}
          onSignatureOptionRemove={onSignatureOptionRemove}
          onSignatureOptionAdd={onSignatureOptionAdd}
          onBarcodeOptionEdit={onBarcodeOptionEdit}
          onBarcodeOptionRemove={onBarcodeOptionRemove}
          onBarcodeOptionAdd={onBarcodeOptionAdd}
        />
      ),
    },
  ];

  const onSubmit = async () => {
    const logbookItems = [];
    for (let i = 0; i < state.currentLogbook.textInputs.length; i++) {
      logbookItems.push({
        active: true,
        inputFieldType: LogbookItemType.TEXT,
        isMandatory: true,
        title: state.currentLogbook.textInputs[i].label,
      });
    }
    for (let i = 0; i < state.currentLogbook.signatureInputs.length; i++) {
      logbookItems.push({
        active: true,
        inputFieldType: LogbookItemType.SIGNATURE,
        isMandatory: true,
        title: state.currentLogbook.signatureInputs[i].label,
      });
    }
    for (let i = 0; i < state.currentLogbook.photoInputs.length; i++) {
      logbookItems.push({
        active: true,
        inputFieldType: LogbookItemType.PHOTO,
        isMandatory: true,
        title: state.currentLogbook.photoInputs[i].label,
      });
    }
    for (let i = 0; i < state.currentLogbook.barcodeInputs.length; i++) {
      logbookItems.push({
        active: true,
        inputFieldType: LogbookItemType.BARCODE,
        isMandatory: true,
        title: state.currentLogbook.barcodeInputs[i].label,
      });
    }
    try {
      const response = await insertLogbook({
        variables: {
          BuildingId: currentBuilding?.Id,
          logbookTimeType: state.currentLogbook.timeType,
          logbookTitle: state.currentLogbook.title,
          logbookItems,
        },
        //refetchQueries: [GET_LOGBOOKS]
      });

      setState(DEFAULT_STATE);
    } catch (e) {
      console.log("🚀 ~ file: logbooks.tsx ~ line 185 ~ onSubmit ~ e", e);
      //TODO: add toast
    }
  };

  const handleTabChange = (newValue: number) => {
    setState({ ...state, tabValue: newValue });
  };

  const add: JSX.Element = (
    <StepperFrame stepFrames={stepFrames} handleSubmit={onSubmit} />
  );
  return (
    <Page title="Logbooks">
      <ListAndAddTabs
        value={state.tabValue}
        handleChange={handleTabChange}
        listChildren={list}
        addChildren={add}
        fromEdit={state.editing}
      />
    </Page>
  );
}
